import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import BotDetailModal from "./BotDetailModal";
import { Row, SCORE_HEADERS, gbp } from "./RobustnessCard";
import { ArrowLeftIcon, SignOutIcon, SealCheckIcon } from "@phosphor-icons/react";

const TABS = [
  { id: "final_exam", label: "Awaiting 2025+ test", color: "#FBBF24" },
  { id: "proven", label: "Passed 2025+", color: "#2ECA7F" },
  { id: "failed_holdout", label: "Failed 2025+", color: "#F87171" },
];
const EXAM = { queued: ["queued", "#FBBF24"], running: ["running now", "#22D3EE"], done: ["done", "#64748B"], pending: ["pending", "#94A3B8"] };

const Tile = ({ testid, label, value, sub, color = "#F1F5F9" }) => (
  <div data-testid={testid} className="border border-[#1F2937] rounded-lg p-4 bg-[#0B0D14]/80">
    <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">{label}</div>
    <div className="text-2xl font-medium mt-1" style={{ color }}>{value}</div>
    {sub && <div className="mono text-[10px] text-[#64748B] mt-1">{sub}</div>}
  </div>
);

const Summary = ({ data }) => {
  const t = data.totals || {};
  const pct = Math.min(100, (100 * (t.best_per_symbol_monthly || 0)) / (t.target || 5000));
  return (
    <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
      <Tile testid="passed-total-monthly" label="Proven basket £/mo (2025+)" value={gbp(t.best_per_symbol_monthly)} color="#2ECA7F"
        sub={`best proven bot per symbol · ${(t.proven_symbols || []).length} symbols`} />
      <Tile testid="passed-gap" label="Gap to £5,000 target" value={gbp(t.gap)} color={t.gap > 0 ? "#FBBF24" : "#2ECA7F"}
        sub={`${pct.toFixed(0)}% of target`} />
      <Tile testid="passed-all-proven" label="All proven bots £/mo" value={gbp(t.all_proven_monthly)}
        sub="sum of every proven bot (overlaps per symbol)" />
      <Tile testid="passed-awaiting" label="Awaiting 2025+ test" value={data.counts?.final_exam || 0} color="#FBBF24"
        sub="queued automatically after the battery" />
      <div className="col-span-2 lg:col-span-4 h-2 rounded-full bg-[#111827] overflow-hidden" data-testid="passed-progress">
        <div className="h-full bg-[#2ECA7F] transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const Filters = ({ data, tab, setTab, symbol, setSymbol }) => (
  <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
    <div className="flex gap-2">
      {TABS.map((t) => (
        <button key={t.id} data-testid={`passed-tab-${t.id}`} onClick={() => setTab(t.id)}
          className="mono text-[11px] px-3 py-1.5 rounded-full border transition-colors"
          style={{ borderColor: `${t.color}${tab === t.id ? "" : "55"}`, color: t.color, background: tab === t.id ? `${t.color}18` : "transparent" }}>
          {t.label} · {data.counts?.[t.id] || 0}
        </button>
      ))}
    </div>
    <select data-testid="passed-symbol-filter" value={symbol} onChange={(e) => setSymbol(e.target.value)}
      className="mono text-xs bg-[#050914] border border-[#1F2937] rounded px-3 py-2 text-[#F1F5F9] focus:outline-none focus:border-[#2ECA7F]">
      <option value="">All symbols</option>
      {(data.symbols || []).map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  </div>
);

const BotsTable = ({ rows, onOpen }) => (
  <div className="mt-4 border border-[#1F2937] rounded-lg overflow-x-auto bg-[#0B0D14]" data-testid="passed-table">
    {rows.length === 0 ? (
      <div className="p-8 text-center mono text-xs text-[#64748B]" data-testid="passed-empty">
        No bots here yet - bots that pass 2022/2023/2024 and every robustness test land here automatically.
      </div>
    ) : (
      <table className="w-full mono text-[11px] whitespace-nowrap">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-[#64748B] border-b border-[#1F2937]">
            {[...SCORE_HEADERS, "2025+ £/mo", "Exam"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => {
            const [ex, exColor] = EXAM[b.exam_status] || EXAM.pending;
            return (
              <Row key={b.bot_name} b={b} onOpen={onOpen} extra={<>
                <td className="px-3 py-2" style={{ color: (b.holdout_monthly ?? 0) > 0 ? "#2ECA7F" : "#F87171" }}>
                  {b.holdout_monthly == null ? "-" : gbp(b.holdout_monthly)}
                </td>
                <td data-testid={`exam-status-${b.bot_name}`} className="px-3 py-2" style={{ color: exColor }}>{ex}</td>
              </>} />
            );
          })}
        </tbody>
      </table>
    )}
  </div>
);

export default function PassedBots() {
  const { logout } = useAuth();
  const [data, setData] = useState({ bots: [], counts: {}, totals: {}, symbols: [] });
  const [tab, setTab] = useState("proven");
  const [symbol, setSymbol] = useState("");
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/mt5admin/brain/passed${symbol ? `?symbol=${symbol}` : ""}`).then((r) => setData(r.data)).catch(() => {});
  }, [symbol]);
  const rows = data.bots.filter((b) => (b.wf || {}).stage === tab);
  return (
    <div data-testid="mt5-passed-page" className="relative min-h-screen bg-[#050914] text-[#F1F5F9]">
      <header className="relative z-10 border-b border-emerald-900/40 backdrop-blur-md bg-[#050914]/80">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/botgenomix-logo-512.png" alt="BotGenomix" className="h-10 w-10 rounded-lg" />
            <div className="text-lg font-medium tracking-tight">
              Bot<span className="text-[#FBBF24]">Genomix</span>
              <span className="ml-2 text-[#2ECA7F] mono text-xs uppercase tracking-widest">Passed bots</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin-mt5" data-testid="passed-back"
              className="mono text-[10px] uppercase tracking-widest text-[#94A3B8] hover:text-[#F1F5F9] flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-900/40">
              <ArrowLeftIcon size={12} /> MT5 Lab
            </Link>
            <button onClick={logout}
              className="mono text-[10px] uppercase tracking-widest text-[#FF4D4D] hover:text-[#ff8080] flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-900/40">
              <SignOutIcon size={12} /> Logout
            </button>
          </div>
        </div>
      </header>
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 pt-10 pb-24">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#2ECA7F] flex items-center gap-2">
          <SealCheckIcon size={14} weight="fill" /> Robustness passed
        </div>
        <h1 className="text-4xl font-medium tracking-tight mt-2">Bots that passed every test.</h1>
        <p className="text-[#94A3B8] mt-3 max-w-3xl">
          Each bot here profited in 2022, 2023 and 2024, survived settings ±20%, double spread and the Monte Carlo
          check. It is then queued automatically for the 2025 → today final exam, data the brain never saw.
        </p>
        <Summary data={data} />
        <Filters data={data} tab={tab} setTab={setTab} symbol={symbol} setSymbol={setSymbol} />
        <BotsTable rows={rows} onOpen={setDetail} />
      </div>
      {detail && <BotDetailModal botName={detail} onClose={() => setDetail(null)} onUse={() => {}} />}
    </div>
  );
}
